import React, { useState } from "react";
import { Button, ButtonVariants, Facility, Facilities } from "@app/shared";
import { EditFacilityDetailsInputs } from "../../../../shared";
import { useModel } from "../../../../../hooks";
import { Icons } from "../../../../shared";

interface CreateLocationStepProps {
    facilityGroupId: string;
    groupName: string;
    onNext: (createdFacilityId: string, facilityName: string | undefined) => void;
    onBack: () => void;
}

export const CreateLocationStep: React.FC<CreateLocationStepProps> = ({ facilityGroupId, groupName, onNext, onBack }) => {
    const [facility, helpers] = useModel(Facility, { facilityGroupId });
    const [isCreating, setIsCreating] = useState(false);

    const handleCreateFacility = async () => {
        setIsCreating(true);
        try {
            await helpers.validateWithAction(async () => {
                const newFacilityId = await Facilities.save(facility as Facility);
                if (newFacilityId) {
                    onNext(newFacilityId, facility.name);
                }
            });
        } catch (error) {
            console.error("Error creating facility:", error);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.LocationOutline className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Create New Location</h2>
                <p className="text-neutral-600 mb-4">Enter the details for your new location where the device will be installed</p>
                <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                    <span className="text-neutral-700 font-medium">Group: {groupName}</span>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-warmGray-100 p-8">
                <div className={helpers.modelContainerClassNames}>
                    <helpers.ErrorSummary />
                    <div className="space-y-6">
                        <EditFacilityDetailsInputs model={facility} helpers={helpers} />
                    </div>
                </div>
            </div>

            <div className="mt-8 flex gap-4">
                <Button variant={ButtonVariants.Secondary} onClick={onBack} className="flex-1 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50">
                    Back
                </Button>
                <Button onClick={handleCreateFacility} disabled={isCreating || !facility.name?.trim()} className="flex flex-1 justify-center items-center bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed">
                    {isCreating ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating...
                        </>
                    ) : (
                        <>
                            <Icons.Success className="w-4 h-4 mr-2" />
                            Create Location
                        </>
                    )}
                </Button>
            </div>

            <div className="mt-8 bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-20 rounded-xl p-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.LightbulbOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-3">Location Setup Tips</h4>
                        <ul className="text-sm text-neutral-600 space-y-2 leading-relaxed">
                            <li className="flex items-start">
                                <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                Use a descriptive name that clearly identifies the location
                            </li>
                            <li className="flex items-start">
                                <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                The address helps with mapping and geographical reference
                            </li>
                            <li className="flex items-start">
                                <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                Location code can be used for easy reference in reports
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
